"use client";

import './accordion-item.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useRef, useEffect } from 'react';
import { validateSurface } from '../../globals.js';
import getOS from '../../utilities/check-os/index.js';
import '../../_virtual/_tslib.js';
import '../../utilities/use-viewport/index.js';
import AccordionHeader from './accordion-item-header.js';
import styles from './accordion-item.module.scss.js';

/**
 * @component AccordionItem
 * @package Accordion
 */
const AccordionItem = ({ header, children, opened = false, alwaysOpen = false, trigger, triggerElement = 'icon', triggerAlignment = 'top', viewport = 'desktop', behavior = 'multiOpen', sticky = false, backgroundColor, disabled, activeAccordionID = '', accordionItemId = '', surface: surfaceProp = 'lightPrimary', onClick, padding = 'standard', 'data-analyticstrack': analyticsTrack, 'data-track': track, 'data-track-ignore': trackIgnore, 'data-clickstream': clickStream, 'data-sitecat-cta': cta, 'data-sitecat-datatrack': dataTrack, 'data-sitecat-position': position, 'data-sitecat-level': level, }) => {
    const [openedState, setOpenedState] = useState(alwaysOpen ? true : opened);
    const accordionItemRef = useRef(null);
    const accordionDetailContentRef = useRef(null);
    const accordionAnimationTrigger = useRef(null);
    useEffect(() => {
        if (behavior === 'singleOpen' &&
            activeAccordionID &&
            activeAccordionID !== accordionItemId &&
            !alwaysOpen &&
            openedState) {
            setOpenedState(false);
        }
    }, [activeAccordionID, accordionItemId, alwaysOpen, behavior, openedState]);
    useEffect(() => {
        if (accordionDetailContentRef.current) {
            const accordionDetailContent = accordionDetailContentRef.current;
            if (openedState) {
                if (accordionAnimationTrigger.current) {
                    clearTimeout(accordionAnimationTrigger.current);
                }
                accordionAnimationTrigger.current = setTimeout(() => {
                    accordionDetailContent.style.overflow = 'visible';
                    accordionDetailContent.removeAttribute('inert');
                }, 350);
            }
            else {
                accordionDetailContent.style.overflow = 'hidden';
                accordionDetailContent.setAttribute('inert', '');
            }
        }
    }, [openedState]);
    useEffect(() => {
        if (!alwaysOpen && opened !== openedState) {
            setOpenedState(opened);
        }
    }, [opened, alwaysOpen]);
    // Normalize the surface prop
    const surface = validateSurface(surfaceProp);
    const accordionItemStyles = [
        styles['accordionItem'],
        styles[surface.toLowerCase()],
        styles[padding.toLowerCase()],
        disabled ? styles.disabled : '',
        openedState ? styles['opened'] : '',
    ];
    const toggleGroup = () => {
        setOpenedState(alwaysOpen ? true : !openedState);
    };
    const _onTrigger = () => {
        if (accordionItemRef === null || accordionItemRef === void 0 ? void 0 : accordionItemRef.current) {
            setTimeout(() => {
                var _a, _b;
                const accordionItemProperties = ((_b = (_a = accordionItemRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) === null || _b === void 0 ? void 0 : _b.top) || 0;
                window.scrollTo({
                    top: accordionItemProperties + window.pageYOffset,
                    behavior: 'smooth',
                });
            }, 350);
        }
        toggleGroup();
        if (onClick && accordionItemId)
            onClick(accordionItemId);
    };
    const renderAccordionDetail = () => {
        const isIos = getOS() === 'ios';
        const accordionItemDetailStyles = [
            styles['accordionItemDetail'],
            styles[surface.toLowerCase()],
            openedState ? styles[padding.toLowerCase()] : '',
            openedState ? styles['opened'] : '',
        ];
        return (jsx("div", { style: { backgroundColor }, className: accordionItemDetailStyles.join(' '), id: `accordionDetail_${accordionItemId || null}`, "aria-labelledby": `accordionHeaderLabel_${accordionItemId}`, tabIndex: isIos || openedState ? undefined : -1, role: "region", "aria-hidden": !openedState && !isIos, "data-testid": "accordion-item-detail-container", children: jsx("div", { ref: accordionDetailContentRef, className: styles.accordionDetailContent, children: children }) }));
    };
    const renderAccordionHeader = () => {
        const accordionHeader = (jsx(AccordionHeader, { header: header, disabled: disabled, trigger: trigger, triggerElement: triggerElement, triggerAlignment: triggerAlignment, viewport: viewport, accordionItemId: accordionItemId, opened: openedState, surface: surface, sticky: sticky, alwaysOpen: alwaysOpen, "data-clickstream": clickStream, "data-analyticstrack": analyticsTrack, "data-track": track, "data-track-ignore": trackIgnore, "data-sitecat-datatrack": dataTrack, "data-sitecat-position": position, "data-sitecat-level": level, "data-sitecat-cta": cta, padding: padding, onTrigger: _onTrigger }));
        return sticky ? (jsx("div", { style: { background: backgroundColor }, className: styles['sticky'], children: accordionHeader })) : (accordionHeader);
    };
    const renderAccordionItem = () => (jsx("div", { style: { background: backgroundColor }, className: styles.accordionItemWrapper, children: jsxs("div", { className: accordionItemStyles.join(' '), ref: accordionItemRef, children: [header && renderAccordionHeader(), children && renderAccordionDetail()] }) }));
    return sticky ? (jsx("div", { "data-testid": accordionItemId ? `stickycontainer_${accordionItemId}` : undefined, children: renderAccordionItem() })) : (renderAccordionItem());
};
AccordionItem.componentType = 'AccordionItem';

export { AccordionItem, AccordionItem as default };
